import { useI18nContext } from '../../i18n/i18n-react';
import {
    CurrentUserRepresentation,
    EntityKey,
    FolderItemType,
    MessageRepresentation,
    MessageStatus,
    MessageThreadRepresentation,
    User,
    UserGroupRepresentation,
} from '@common/api';
import { Button } from '../../components/button';
import { getClusterPathFromAnchor, useCommentsActions, useRepliesByMessageThreadId } from '@data/hooks/comments';
import { MentionData } from '@draft-js-plugins/mention';
import { ArrowRight, ChevronLeft, ChevronRight, Clock, Tick } from '@components/icons/FarseerIcons';
import { Close } from '@components/icons/FarseerIcons';
import { Delete } from '@components/icons/FarseerIcons';
import { IStatus, Status } from '@components/status/Status';
import { Avatar } from '@components/avatar/Avatar';
import { Tags } from '@features/tags/Tags';
import { TagBadge } from '@components/tag/TagBadge';
import { FlexCol, FlexRow } from '@components/layout/Flex';
import {
    Comment,
    CommentAvatarBox,
    CommentAvatarLine,
    Message,
    MessageDate,
    MessageHeader,
    ShowRepliesBtn,
} from './Comments.style';
import { appColors } from '@base/useTheme';
import { RawDraftContentState } from 'draft-js';
import { CSSProperties, ReactElement, useEffect, useRef, useState } from 'react';
import { FaEllipsisH } from 'react-icons/fa';
import { Toggle } from '@components/toggle/Toggle';
import { downloadFarseerFile } from '@data/farseerFile/farseerFileActions';
import { getFullName, getInitials } from '@data/user/user';
import { formatDateWithFormat } from '@util/date';
import { downloadFile } from '../../util/helpers';
import { Attachment, AttachmentInput } from './Attachments';
import { MentionInput, Mentions } from './Mentions';
import { useFoldersActions } from '@data/folder/folderActions';
import { useNavigateWithParams } from '@hooks/useNavigateWithParams';
import { pagePath } from '@pages/pagePaths';
import { ContextMenu, useContextMenuState } from '@components/context-menu/ContextMenu';
import { GeneratedEntityType } from '@data/entity/types';

export const COMMMENT_TEXTAREA: CSSProperties = {
    padding: '8.5px 10px',
    flexGrow: 1,
    cursor: 'text',
    borderRadius: 5,
    maxWidth: 240,
    backgroundColor: appColors.neutral.w100,
};

const STATUS_COMMON_STYLE: CSSProperties = {
    display: 'flex',
    fontSize: 11,
    textAlign: 'center',
    alignItems: 'center',
};

const MESSAGE: CSSProperties = {
    margin: '11px 0px',
    maxWidth: 208,
    cursor: 'text',
};

export interface CommentThreadArrowsProps {
    current: number;
    all: number;
    action: (idx: number) => void;
}

export interface CommentThreadProps {
    messageThread: MessageThreadRepresentation;
    mentionData: MentionData[];
    currentUser?: CurrentUserRepresentation;
    hasBorder?: boolean;
    showBadges?: string[];
    showEntities?: EntityKey[];
    arrows?: CommentThreadArrowsProps | null;
    maxHeight?: number | null;
    userMapById: Map<number, User>;
    showMoreOptions?: boolean;
    isAddingMessageThread?: boolean;
}

const StatusMap: { [K in MessageStatus]: IStatus } = {
    [MessageStatus.Resolved]: {
        status: 'Resolved',
        value: MessageStatus.Resolved,
        style: {
            ...STATUS_COMMON_STYLE,
            color: appColors.primary.w500,
            fontWeight: 'bold',
        },
    },
    [MessageStatus.Unresolved]: {
        status: 'Unresolved',
        value: MessageStatus.Unresolved,
        default: true,
        style: { ...STATUS_COMMON_STYLE, color: appColors.neutral.w700 },
    },
    [MessageStatus.InReview]: {
        status: 'In Review',
        value: MessageStatus.InReview,
        style: { ...STATUS_COMMON_STYLE, color: appColors.warning.w500, fontWeight: 'bold' },
    },
};

export enum MessageThreadType {
    General = 'General',
    Cluster = 'Cluster',
}

function toMentionUser(user: User): MentionData {
    return {
        id: 'user' + user.id,
        itemId: user.id,
        name: `${user.firstName} ${user.lastName}`,
        isGroup: false,
        userIds: null,
    };
}
function toMentionGroup(group: UserGroupRepresentation): MentionData {
    return {
        id: 'group' + group.id,
        itemId: group.id,
        name: `${group.name}`,
        isGroup: true,
        userIds: group.userIds,
    };
}

export function getMentionData(users: User[], groups: UserGroupRepresentation[]) {
    return users.map(toMentionUser).concat(groups.map(toMentionGroup));
}

export function createUserByIdMap(users: User[]) {
    return new Map(users.map((u) => [u.id, u]));
}

function StatusOptions({ currentStatus }: { currentStatus: IStatus }) {
    return (
        <div>
            <Status {...currentStatus} />
        </div>
    );
}

function ReplyItem({ reply, isLastReply, user }: { reply: MessageRepresentation; isLastReply: boolean; user?: User }) {
    const [filesDownloadingById, setFilesDownloadingById] = useState<Partial<Record<string, boolean>>>({});

    return (
        <Message>
            <CommentAvatarBox>
                <Avatar size={32} initials={getInitials(user)} imageUrl={user?.avatarUrl} />
                {isLastReply ? null : <CommentAvatarLine />}
            </CommentAvatarBox>
            <FlexCol style={{ flexGrow: 1 }}>
                <MessageHeader>
                    <label style={{ fontWeight: 'bold' }}>{getFullName(user)}</label>
                </MessageHeader>
                <MessageHeader>
                    <MessageDate>{formatDateWithFormat(reply.creationTime, 'HH:mm, MMM Do', true)}</MessageDate>
                </MessageHeader>
                <Mentions
                    style={MESSAGE}
                    rawContent={reply.content}
                    footer={
                        !!reply.files?.length && (
                            <div style={{ display: 'flex', flexWrap: 'wrap', marginTop: 8 }}>
                                {reply?.files?.map((f) => (
                                    <Attachment
                                        key={f.id}
                                        id={f.id}
                                        name={f.name}
                                        loading={!!filesDownloadingById?.[f.id]}
                                        onDownload={async (id) => {
                                            setFilesDownloadingById({ ...filesDownloadingById, [id]: true });
                                            const blob = await downloadFarseerFile(id);
                                            downloadFile(blob, f.name);
                                            setFilesDownloadingById({ ...filesDownloadingById, [id]: false });
                                        }}
                                    />
                                ))}
                            </div>
                        )
                    }
                />
            </FlexCol>
        </Message>
    );
}

function createMessageKey(message: MessageThreadRepresentation | MessageRepresentation) {
    return message.creationTime.toISOString() + message.creatorId;
}

export default function CommentThread({
    messageThread,
    mentionData,
    currentUser,
    hasBorder = false,
    showBadges = [],
    showEntities = [],
    arrows = null,
    maxHeight = null,
    userMapById,
}: CommentThreadProps): ReactElement {
    const { LL } = useI18nContext();
    const navigate = useNavigateWithParams();
    const toggleRef = useRef<HTMLButtonElement>(null);
    const [attachments, setAttachments] = useState<File[]>([]);
    const [showReplies, setShowReplies] = useState(false);
    const { appendMessageToThread, changeCommentStatus, readComment, removeMessageThread, isAppendingMessageThread } =
        useCommentsActions();
    const { lookForFolders } = useFoldersActions();
    const [filesDownloadingById, setFilesDownloadingById] = useState<Partial<Record<string, boolean>>>({});
    const { menuState, menuHandlers } = useContextMenuState({ click: 'left' });

    const user = userMapById.get(messageThread.creatorId ?? 0);
    const replies = useRepliesByMessageThreadId(messageThread.id);
    const initialMessage = messageThread.metadata.initialMessage;
    const initialMessageKey = createMessageKey(initialMessage);
    const lastMessage = messageThread.metadata.lastMessage;

    const status: IStatus = StatusMap[initialMessage.status || MessageStatus.Unresolved];

    useEffect(() => {
        if (currentUser === undefined) {
            return;
        }
        const cursorForCurrUser = messageThread.metadata.cursors[currentUser.id] as number | undefined;
        if (cursorForCurrUser === undefined || cursorForCurrUser < lastMessage.order) {
            readComment({ messageThread, lastMessageOrder: lastMessage.order });
        }
    }, [currentUser, messageThread, lastMessage, readComment]);

    async function createReplyHandler(
        mentionedUserIds: number[],
        mentionedGroupIds: number[],
        rawContent: RawDraftContentState
    ) {
        if ((!rawContent.blocks.some((b) => !!b.text) && !attachments.length) || isAppendingMessageThread) {
            return;
        }
        const content = JSON.stringify(rawContent);

        await appendMessageToThread({
            threadId: messageThread.id,
            anchor: messageThread.anchor,
            message: {
                content,
                mentionedUserIds: mentionedUserIds,
                mentionedUserGroupIds: mentionedGroupIds,
            },
            files: attachments.length ? attachments : undefined,
        });
        setAttachments([]);
    }

    async function pathToComment(anchor: string) {
        const clusterPath = getClusterPathFromAnchor(anchor);
        const sheetId = parseInt(clusterPath.split('_')[0], 10);
        const folderItem = await lookForFolders([{ reference: sheetId.toString(), type: FolderItemType.Sheet }]);
        const folderItemId = folderItem[0].id;
        const folderId = folderItem[0].path[folderItem[0].path.length - 1];
        // Another .split('//') to remove row name if the comment was on a row instead of a cell
        return `${pagePath.Sheet}/${folderId}/${folderItemId}/${sheetId}/${clusterPath.split('//')[0]}`;
    }

    const anchorMenuItems = messageThread.anchor ? (
        <>
            <ContextMenu.Item
                item={{
                    icon: ArrowRight,
                    label: LL.GO_TO_ROW(),
                    async action() {
                        if (messageThread.anchor) {
                            navigate(await pathToComment(messageThread.anchor));
                        }
                    },
                }}
            />
            <ContextMenu.Divider />
        </>
    ) : null;

    const actionsMap = {
        [MessageStatus.Resolved]: (
            <>
                <ContextMenu.Item
                    item={{
                        label: LL.MARK_IN_REVIEW(),
                        icon: Clock,
                        action() {
                            changeCommentStatus({ messageThread, status: MessageStatus.InReview });
                        },
                    }}
                />
                <ContextMenu.Item
                    item={{
                        label: LL.MARK_AS_UNRESOLVED(),
                        icon: Close,
                        action() {
                            changeCommentStatus({ messageThread, status: MessageStatus.Unresolved });
                        },
                    }}
                />
                <ContextMenu.Divider />
            </>
        ),
        [MessageStatus.InReview]: (
            <>
                <ContextMenu.Item
                    item={{
                        label: LL.MARK_AS_RESOLVED(),
                        icon: Tick,
                        action() {
                            changeCommentStatus({ messageThread, status: MessageStatus.Resolved });
                        },
                    }}
                />
                <ContextMenu.Item
                    item={{
                        label: LL.MARK_AS_UNRESOLVED(),
                        icon: Close,
                        action() {
                            changeCommentStatus({ messageThread, status: MessageStatus.Unresolved });
                        },
                    }}
                />
                <ContextMenu.Divider />
            </>
        ),
        [MessageStatus.Unresolved]: (
            <>
                <ContextMenu.Item
                    item={{
                        label: LL.MARK_AS_RESOLVED(),
                        icon: Tick,
                        action() {
                            changeCommentStatus({ messageThread, status: MessageStatus.Resolved });
                        },
                    }}
                />
                <ContextMenu.Item
                    item={{
                        label: LL.MARK_IN_REVIEW(),
                        icon: Clock,
                        action() {
                            changeCommentStatus({ messageThread, status: MessageStatus.InReview });
                        },
                    }}
                />
                <ContextMenu.Divider />
            </>
        ),
    };

    return (
        <Comment $border={hasBorder}>
            {showBadges.length > 0 && (
                <FlexRow $wrap>
                    {showBadges.map((badge, idx) => {
                        return <TagBadge key={idx} type={GeneratedEntityType.Literal} label={badge} />;
                    })}
                </FlexRow>
            )}
            {showEntities.length > 0 && (
                <FlexRow $wrap>
                    <Tags tags={showEntities} />
                </FlexRow>
            )}
            <div style={maxHeight ? { maxHeight: maxHeight, overflow: 'auto' } : {}}>
                <Message>
                    <CommentAvatarBox>
                        <Avatar
                            color="white"
                            backgroundColor={appColors.ui.dark}
                            size={32}
                            initials={getInitials(user)}
                            imageUrl={user?.avatarUrl}
                        />
                        <CommentAvatarLine />
                    </CommentAvatarBox>
                    <FlexCol style={{ flexGrow: 1 }}>
                        <MessageHeader>
                            <label style={{ fontWeight: 'bold' }}>{getFullName(user)}</label>
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'end' }}>
                                <StatusOptions currentStatus={status} />
                                <Toggle
                                    disabled={isAppendingMessageThread}
                                    style={{ marginLeft: 10 }}
                                    $size="small"
                                    ref={toggleRef}
                                    icon={() => <FaEllipsisH />}
                                    {...menuHandlers}
                                />

                                <ContextMenu {...menuState} placement="bottom-start" anchorRef={toggleRef}>
                                    {actionsMap[initialMessage.status]}
                                    {anchorMenuItems}
                                    <ContextMenu.Item
                                        item={{
                                            icon: Delete,
                                            label: LL.DELETE_THREAD(),
                                            async action() {
                                                await removeMessageThread(messageThread);
                                            },
                                            disabled: messageThread.creatorId !== currentUser?.id,
                                        }}
                                    />
                                </ContextMenu>
                            </div>
                        </MessageHeader>
                        <MessageHeader>
                            <MessageDate>
                                {formatDateWithFormat(initialMessage.creationTime, 'HH:mm, MMM Do', true)}
                            </MessageDate>
                        </MessageHeader>
                        <Mentions
                            key={messageThread.id}
                            style={MESSAGE}
                            rawContent={initialMessage.content}
                            footer={
                                !!messageThread.metadata.initialMessage.files?.length && (
                                    <div style={{ display: 'flex', flexWrap: 'wrap', marginTop: 8 }}>
                                        {messageThread.metadata.initialMessage?.files?.map((f) => (
                                            <Attachment
                                                key={f.id}
                                                id={f.id}
                                                name={f.name}
                                                loading={!!filesDownloadingById?.[f.id]}
                                                onDownload={async (id) => {
                                                    setFilesDownloadingById({ ...filesDownloadingById, [id]: true });
                                                    const blob = await downloadFarseerFile(id);
                                                    downloadFile(blob, f.name);
                                                    setFilesDownloadingById({ ...filesDownloadingById, [id]: false });
                                                }}
                                            />
                                        ))}
                                    </div>
                                )
                            }
                        />
                    </FlexCol>
                </Message>
                {showReplies &&
                    replies.map((reply: MessageRepresentation, idx: number) => {
                        const currentMessageKey = createMessageKey(reply);
                        if (currentMessageKey !== initialMessageKey) {
                            return (
                                <ReplyItem
                                    key={currentMessageKey}
                                    reply={reply}
                                    isLastReply={idx === replies.length - 1}
                                    user={userMapById.get(reply.creatorId ?? 0)}
                                />
                            );
                        }
                    })}
                {!showReplies && (
                    <>
                        {replies.length > 5 && (
                            <ShowRepliesBtn onClick={() => setShowReplies(true)}>...</ShowRepliesBtn>
                        )}
                        {replies.slice(-5).map((reply: MessageRepresentation, idx: number) => {
                            const currentMessageKey = createMessageKey(reply);
                            if (currentMessageKey !== initialMessageKey) {
                                return (
                                    <ReplyItem
                                        key={currentMessageKey}
                                        reply={reply}
                                        isLastReply={idx === replies.length - 1}
                                        user={userMapById.get(reply.creatorId ?? 0)}
                                    />
                                );
                            }
                        })}
                    </>
                )}
                <FlexRow>
                    <CommentAvatarBox>
                        <Avatar size={32} initials={getInitials(currentUser)} imageUrl={currentUser?.avatarUrl} />
                    </CommentAvatarBox>
                    <div
                        style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexGrow: 1 }}
                    >
                        <MentionInput
                            mentionData={mentionData}
                            style={COMMMENT_TEXTAREA}
                            placeholder={LL.REPLAY_OR_TAG_IN_COMMENT()}
                            onDone={createReplyHandler}
                            footer={
                                !!attachments.length && (
                                    <div style={{ display: 'flex', flexWrap: 'wrap', marginTop: 8 }}>
                                        {attachments.map((a, idx) => (
                                            <Attachment
                                                loading={isAppendingMessageThread}
                                                key={idx}
                                                id={idx}
                                                name={a.name}
                                                onRemove={(id) =>
                                                    setAttachments(attachments.filter((a, i) => i !== id))
                                                }
                                            />
                                        ))}
                                    </div>
                                )
                            }
                        />
                        <AttachmentInput
                            disabled={isAppendingMessageThread}
                            onChange={(e) => {
                                const files: File[] = [];
                                for (let i = 0; i < (e.target.files?.length ?? 0); i++) {
                                    const file = e.target.files?.item(i);
                                    if (file) {
                                        files.push(file);
                                    }
                                }

                                setAttachments([...attachments, ...files]);
                            }}
                        />
                    </div>
                </FlexRow>
            </div>
            {arrows && (
                <FlexRow style={{ alignItems: 'center' }}>
                    <div style={{ flexGrow: 1, paddingLeft: 44 }}>
                        {LL.COMMENT_OF({ arrowsCurrent: arrows.current, arrowsAll: arrows.all })}
                    </div>
                    <Button
                        $size="small"
                        $type="quaternary"
                        onClick={() => arrows.action(arrows.current - 1)}
                        icon={ChevronLeft}
                    />
                    <Button
                        $size="small"
                        $type="quaternary"
                        onClick={() => arrows.action(arrows.current + 1)}
                        icon={ChevronRight}
                    />
                </FlexRow>
            )}
        </Comment>
    );
}
